'use es6';

import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { getSnapshot } from 'DesignData/selectors/layouts';
import { getWidgetAncestry } from 'DesignData/selectors/layoutData';
import { getSelectedWidgets } from 'DesignData/selectors/layoutEditorSession';
import { makeReduceWidgetIdsToContentOverridden } from 'DesignData/selectors/layoutCosIndexConnections';
import { fetch as fetchLayout } from 'DesignData/actions/UnifiedLayouts';
const reduceWidgetIdsToContentOverridden = makeReduceWidgetIdsToContentOverridden();
/*
 *
 * Utils
 *
 */

function mapWidgetIdsWithContextData(state, layoutId, widgetIds) {
  const props = {
    layoutId
  };
  const ancestryMap = getWidgetAncestry(state, props);
  return widgetIds.map(widgetId => {
    const node = ancestryMap[widgetId];
    const {
      rowId,
      parentId
    } = node.data;
    return {
      widgetId,
      rowId,
      parentId
    };
  });
}
/*
 *
 * Action Creators
 *
 */

/**
 * Hard mutating action WITHOUT a confirmation.
 *
 * @param {String}          layoutId
 * @param {Array.<String>}  widgetIds
 * @param {Object}          extraParams - Optional extra props to be passed in action message.
 */


function __hardMutateWidgets(type, layoutId, widgetIds, extraParams) {
  return (dispatch, getState) => {
    const widgets = mapWidgetIdsWithContextData(getState(), layoutId, widgetIds);
    return dispatch({
      type,
      layoutId,
      widgets,
      data: extraParams || EMPTY_OBJECT
    });
  };
}
/**
 *
 * @param {Number|String}   layoutId
 * @param {Array.<String>}  widgetIds
 * @param {Object}          extraParams - Optional extra props to be passed in action message.
 */


function __stageWidgetsForMutatingConfirmation(type, layoutId, widgetIds, extraParams) {
  return {
    type,
    layoutId,
    widgetIds,
    data: extraParams || EMPTY_OBJECT
  };
}
/**
 *
 * @param {Number|String}   layoutId
 * @param {Object}          extraParams - Optional extra props to be passed in action message.
 */


function __unstageWidgetsForMutatingConfirmation(type, layoutId, extraParams) {
  return {
    type,
    layoutId,
    data: extraParams || EMPTY_OBJECT
  };
}
/**
 *
 * @param {Number|String}   layoutId
 * @param {Array.<String>}  widgetIds
 * @param {Object}          snapshot
 * @param {Object}          extraParams - Optional extra props to be passed in action message.
 */


function __revertWidgetMutations(type, layoutId, widgetIds, snapshot, extraParams) {
  return {
    type,
    layoutId,
    widgetIds,
    snapshot,
    data: extraParams || EMPTY_OBJECT
  };
}
/**
 * Returns a set of action creator to be used for optimistic deletion/mutation of
 * widgets given content overridden data queried from COS Index.
 *
 * @param {Object} actionTypes
 * @param {String} actionTypes.hardActionType - ActionType
 * @param {String} actionTypes.optimisticConfirmationActionType - ActionType
 * @param {String} actionTypes.revertConfirmationActionType - ActionType
 */


export function createOptimisticContentMutatingWidgetActions({
  hardActionType,
  optimisticConfirmationActionType,
  revertConfirmationActionType,
  includeAncestry = true
}) {
  const hardMutateWidgets = __hardMutateWidgets.bind(null, hardActionType);

  const stageWidgetsForMutatingConfirmation = __stageWidgetsForMutatingConfirmation.bind(null, optimisticConfirmationActionType);

  const unstageWidgetsForMutatingConfirmation = __unstageWidgetsForMutatingConfirmation.bind(null, optimisticConfirmationActionType);

  const revertWidgetMutations = __revertWidgetMutations.bind(null, revertConfirmationActionType);
  /**
   *
   * @param {Number|String}   layoutId
   * @param {Array.<String>}  widgetIds
   * @param {Object}          extraParams - Optional extra props to be passed in action message.
   */


  function __optimisticMutateWidgets(layoutId, widgetIds, extraParams) {
    return (dispatch, getState) => {
      let state = getState();
      const props = {
        layoutId,
        widgetIds,
        includeAncestry
      };
      const snapshot = getSnapshot(state, props);
      dispatch(hardMutateWidgets(layoutId, widgetIds, extraParams));
      const promise = dispatch(fetchLayout(layoutId));
      return promise.then(() => {
        // DO NOT __optimisticMutateWidgets recursively. Potential for infinite loop.
        state = getState();
        const mutationsWithContent = reduceWidgetIdsToContentOverridden(state, props);
        if (!mutationsWithContent.length) return;
        dispatch(revertWidgetMutations(layoutId, widgetIds, snapshot, extraParams));
        dispatch(stageWidgetsForMutatingConfirmation(layoutId, widgetIds, extraParams));
      });
    };
  }
  /**
   *
   * @param {Function}        dispatch
   * @param {Function}        getState
   * @param {Number|String}   layoutId
   * @param {Array.<String>}  widgetIds
   * @param {Object}          extraParams - Optional extra props to be passed in action message.
   */


  function __mutateWidgets(dispatch, getState, layoutId, widgetIds, extraParams) {
    const state = getState();
    const props = {
      layoutId,
      widgetIds,
      includeAncestry
    };
    const mutationsWithContent = reduceWidgetIdsToContentOverridden(state, props); // Widgets WITH content.

    if (mutationsWithContent.length) {
      // Refetch layout so the confirmation modal will have the latest data on
      // modules with content overridden.
      dispatch(fetchLayout(layoutId));
      return dispatch(stageWidgetsForMutatingConfirmation(layoutId, widgetIds, extraParams));
    } // Widgets WITHOUT content.


    return dispatch(__optimisticMutateWidgets(layoutId, widgetIds, extraParams));
  }
  /**
   * Mutate multiple widgets.
   *
   * @param {String|Number}   layoutId - Id of layout.
   * @param {Array.<String>}  widgetIds - Ids of widgets to mutate.
   * @param {Object}          extraParams - Optional extra props to be passed in action message.
   */


  function mutateMultipleWidgets(layoutId, widgetIds, extraParams) {
    return (dispatch, getState) => {
      return __mutateWidgets(dispatch, getState, layoutId, widgetIds, extraParams);
    };
  }
  /**
   * Mutate widget.
   *
   * @param {String|Number}   layoutId - Id of layout with widget.
   * @param {String}          widgetId - Id of widget to mutate.
   * @param {Object}          extraParams - Optional extra props to be passed in action message.
   */


  function mutateWidget(layoutId, widgetId, extraParams) {
    return mutateMultipleWidgets(layoutId, [widgetId], extraParams);
  }
  /**
   * Mutate selected widgets.
   *
   * @param {String|Number}   layoutId - Id of layout.
   * @param {Object}          extraParams - Optional extra props to be passed in action message.
   */


  function mutateSelectedWidgets(layoutId, extraParams) {
    return (dispatch, getState) => {
      const state = getState();
      const selectedWidgets = getSelectedWidgets(state, {
        layoutId
      });
      const widgetIds = selectedWidgets.order;
      return dispatch(mutateMultipleWidgets(layoutId, widgetIds, extraParams));
    };
  }

  return {
    // Three primary action creators for layout editor menus and commands.
    mutateWidget,
    mutateMultipleWidgets,
    mutateSelectedWidgets,

    /** Action creator that triggers mutation w/o optimistic follow up. */
    hardMutateWidgets,

    /** Action creator to populate data for optimistic confirmation. */
    stageWidgetsForMutatingConfirmation,

    /** Action creator to remove data for optimistic confirmation. */
    unstageWidgetsForMutatingConfirmation,

    /** Action to undo mutation. */
    revertWidgetMutations
  };
}